import { useState } from 'react';
import { playlistService } from '@/services/playlist.service';
import { ContentStatus } from '@/types/contentItem';
import { Playlist, PlaylistFormData } from '@/types/playlist';

interface PlaylistFilters {
  status?: ContentStatus;
  search?: string;
}

export const usePlaylists = () => {
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const limit = 20;

  const listPlaylists = async (overrides?: { offset?: number; filters?: PlaylistFilters }) => {
    const nextOffset = overrides?.offset ?? offset;
    setLoading(true);
    try {
      const result = await playlistService.listPlaylists({
        limit,
        offset: nextOffset,
        ...overrides?.filters,
      });
      setPlaylists(result.data);
      setTotal(result.total);
      setError(false);
    } catch (err) {
      setError(true);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createPlaylist = async (data: PlaylistFormData): Promise<Playlist> => {
    const result = await playlistService.createPlaylist(data);
    await listPlaylists();
    return result;
  };

  const updatePlaylist = async (id: string, data: PlaylistFormData): Promise<Playlist> => {
    const result = await playlistService.updatePlaylist(id, data);
    await listPlaylists();
    return result;
  };

  const deletePlaylist = async (id: string): Promise<void> => {
    await playlistService.deletePlaylist(id);
    await listPlaylists();
  };

  const togglePublish = async (id: string, currentStatus: ContentStatus): Promise<void> => {
    setTogglingId(id);
    try {
      if (currentStatus === ContentStatus.PUBLISHED) {
        await playlistService.unpublishPlaylist(id);
      } else {
        await playlistService.publishPlaylist(id);
      }
      await listPlaylists();
    } finally {
      setTogglingId(null);
    }
  };

  return {
    playlists,
    loading,
    error,
    total,
    offset,
    setOffset,
    limit,
    togglingId,
    listPlaylists,
    createPlaylist,
    updatePlaylist,
    deletePlaylist,
    togglePublish,
  };
};
